'use client';

import React, { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

interface ShareGreetingDialogProps {
    isOpen: boolean;
    onClose: () => void;
    shareUrl: string;
    /** Locale for language support: 'vn' | 'en' */
    locale?: string;
}

export default function ShareGreetingDialog({ isOpen, onClose, shareUrl, locale = 'vn' }: ShareGreetingDialogProps) {
    const qrRef = useRef<HTMLDivElement>(null);
    const [copied, setCopied] = useState<string | null>(null);

    // Check if locale is English
    const isEnglish = locale?.toLowerCase() === 'en';

    // Translations
    const t = {
        shareTitle: isEnglish ? 'New Year Greeting Card' : 'Thiếp chúc Tết',
        shareText: isEnglish ? 'A special New Year greeting just for you! 🎊🧧' : 'Thiếp chúc Tết đặc biệt dành cho bạn! 🎊🧧',
        copiedLink: isEnglish ? '✓ Link copied!' : '✓ Đã sao chép link!',
        copiedQR: isEnglish ? '✓ QR copied!' : '✓ Đã sao chép QR!',
        shareThis: isEnglish ? 'Share this card' : 'Chia sẻ thiếp này',
        copyQR: isEnglish ? 'Copy QR' : 'Sao chép QR',
        downloadQR: isEnglish ? 'Download QR' : 'Tải QR',
        copyLink: isEnglish ? 'Copy link' : 'Sao chép link',
        shareOnSocial: isEnglish ? 'Share on social media' : 'Chia sẻ trên mạng xã hội',
        shareViaApp: isEnglish ? 'Share via app' : 'Chia sẻ qua ứng dụng',
        close: isEnglish ? 'Close' : 'Đóng',
    };

    const isMobile = () =>
        typeof window !== 'undefined' &&
        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const canNativeShare =
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function' &&
        isMobile();

    const handleNativeShare = async () => {
        if (!navigator.share) return;

        try {
            await navigator.share({
                title: t.shareTitle,
                text: t.shareText,
                url: shareUrl,
            });
        } catch (err) {
            console.log('User cancelled or error:', err);
        }
    };

    if (!isOpen) return null;

    const showCopiedFeedback = (type: string) => {
        setCopied(type);
        setTimeout(() => setCopied(null), 1500);
    };

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            showCopiedFeedback('link');
        } catch {
            // fallback
            const textArea = document.createElement('textarea');
            textArea.value = shareUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            showCopiedFeedback('link');
        }
    };

    const handleCopyQR = async () => {
        try {
            const canvas = qrRef.current?.querySelector('canvas');
            if (!canvas) return;
            canvas.toBlob(async (blob) => {
                if (blob) {
                    await navigator.clipboard.write([
                        new ClipboardItem({ 'image/png': blob })
                    ]);
                    showCopiedFeedback('qr');
                }
            });
        } catch {
            showCopiedFeedback('qr-error');
        }
    };

    const handleDownloadQR = () => {
        try {
            const canvas = qrRef.current?.querySelector('canvas');
            if (!canvas) return;
            const link = document.createElement('a');
            link.download = isEnglish ? 'new-year-greeting-qr.png' : 'thiep-chuc-tet-qr.png';
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (error) {
            console.error('Download QR failed:', error);
        }
    };

    const handleSocialShare = (platform: string) => {
        const encodedUrl = encodeURIComponent(shareUrl);
        const text = encodeURIComponent(t.shareText);
        let url = '';

        switch (platform) {
            case 'facebook':
                url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
                break;
            case 'messenger':
                url = `https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=291494419107518&redirect_uri=${encodedUrl}`;
                break;
            case 'telegram':
                url = `https://t.me/share/url?url=${encodedUrl}&text=${text}`;
                break;
            case 'whatsapp':
                url = `https://wa.me/?text=${text}%20${encodedUrl}`;
                break;
            case 'linkedin':
                url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
                break;
            default:
                return;
        }
        window.open(url, '_blank', 'noopener,noreferrer,width=600,height=500');
    };

    return (
        <div className="share-greeting-overlay" onClick={onClose}>
            <div className="share-greeting-card" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
                {/* Copied feedback */}
                {copied === 'link' && <div className="share-greeting-copied">{t.copiedLink}</div>}
                {copied === 'qr' && <div className="share-greeting-copied">{t.copiedQR}</div>}

                {/* Header */}
                <div className="share-greeting-header">
                    <h3>{t.shareThis}</h3>
                    <button onClick={onClose} aria-label={t.close}>✕</button>
                </div>

                {/* QR Code */}
                <div className="share-greeting-qr">
                    <div ref={qrRef} className="share-greeting-qr-wrapper">
                        <QRCodeCanvas
                            value={shareUrl}
                            size={140}
                            bgColor="#ffffff"
                            fgColor="#8B0606"
                            level="M"
                            includeMargin={true}
                        />
                    </div>

                    <div className="share-greeting-qr-actions">
                        <button className="share-greeting-qr-btn" onClick={handleCopyQR}>
                            📋 {t.copyQR}
                        </button>
                        <button className="share-greeting-qr-btn" onClick={handleDownloadQR}>
                            ⬇️ {t.downloadQR}
                        </button>
                    </div>
                </div>

                {/* Copy URL */}
                <div className="share-greeting-link">
                    <button className="share-greeting-link-btn" onClick={handleCopyUrl}>
                        🔗 {t.copyLink}
                    </button>
                </div>

                {/* Social Media */}
                <div className="share-greeting-social">
                    <p>{t.shareOnSocial}</p>
                    <div className="share-greeting-social-icons">
                        <button
                            className="share-greeting-social-btn"
                            onClick={() => handleSocialShare('facebook')}
                            title="Facebook"
                        >
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                                <circle cx="20" cy="20" r="20" fill="#1877F2" />
                                <path d="M27.5 20H23.5V17C23.5 15.9 24.4 15.5 25 15.5H27.5V11H23.5C20.5 11 18.5 13.5 18.5 17V20H15V24.5H18.5V35H23.5V24.5H26.5L27.5 20Z" fill="white" />
                            </svg>
                        </button>
                        <button
                            className="share-greeting-social-btn"
                            onClick={() => handleSocialShare('messenger')}
                            title="Messenger"
                        >
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                                <circle cx="20" cy="20" r="20" fill="url(#messengerGrad)" />
                                <path d="M20 8C13.4 8 8 12.8 8 19C8 22.4 9.6 25.2 12.2 27V32L16.8 29.4C17.8 29.6 18.8 29.8 20 29.8C26.6 29.8 32 25 32 18.8C32 12.8 26.6 8 20 8ZM21.4 22.2L18 18.6L11.4 22.2L18.6 14.6L22.2 18.2L28.6 14.6L21.4 22.2Z" fill="white" />
                                <defs><linearGradient id="messengerGrad" x1="20" y1="0" x2="20" y2="40"><stop stopColor="#00B2FF" /><stop offset="1" stopColor="#006AFF" /></linearGradient></defs>
                            </svg>
                        </button>
                        <button
                            className="share-greeting-social-btn"
                            onClick={() => handleSocialShare('telegram')}
                            title="Telegram"
                        >
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                                <circle cx="20" cy="20" r="20" fill="#26A5E4" />
                                <path d="M9.5 19.5L29.5 11.5L26.5 30L19 23.5L26.5 15L16.5 24.5L9.5 19.5Z" fill="white" />
                            </svg>
                        </button>
                        <button
                            className="share-greeting-social-btn"
                            onClick={() => handleSocialShare('whatsapp')}
                            title="WhatsApp"
                        >
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                                <circle cx="20" cy="20" r="20" fill="#25D366" />
                                <path d="M20 8C13.4 8 8 13.4 8 20C8 22.4 8.8 24.6 10 26.4L8.4 32L14.2 30.4C16 31.4 17.8 32 20 32C26.6 32 32 26.6 32 20C32 13.4 26.6 8 20 8ZM26.4 25C26 26.2 24.2 27.2 23.2 27.4C22.2 27.6 21 27.6 19.8 27.2C18.6 26.8 16.6 26 14.8 24.2C12.8 22.2 11.6 19.6 11.4 19.2C11.2 18.8 10 17 10 15.2C10 13.4 10.8 12.6 11.2 12C11.6 11.4 12 11.4 12.4 11.4C12.8 11.4 13 11.4 13.4 11.4C13.8 11.4 14.2 11.2 14.6 12.2C15 13.2 15.8 15 15.8 15.2C15.8 15.4 16 15.8 15.8 16C15.6 16.2 15.4 16.6 15.2 16.8C15 17 14.8 17.2 14.6 17.4C14.6 17.6 15 18.2 15.8 19.2C16.8 20.4 17.8 20.8 18.4 21C19 21.2 19.2 21 19.4 20.8C19.6 20.6 20.4 19.6 20.6 19.2C20.8 18.8 21.2 18.8 21.6 19C22 19.2 23.8 20 24.2 20.2C24.6 20.4 24.8 20.6 24.8 20.8C25 21.2 25 22 26.4 25Z" fill="white" />
                            </svg>
                        </button>
                        <button
                            className="share-greeting-social-btn"
                            onClick={() => handleSocialShare('linkedin')}
                            title="LinkedIn"
                        >
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                                <circle cx="20" cy="20" r="20" fill="#0A66C2" />
                                <path d="M13 17H16V28H13V17ZM14.5 11C15.6 11 16.5 11.9 16.5 13C16.5 14.1 15.6 15 14.5 15C13.4 15 12.5 14.1 12.5 13C12.5 11.9 13.4 11 14.5 11ZM18 17H21V18.6C21.6 17.6 22.8 16.8 24.6 16.8C28 16.8 28.5 19 28.5 22V28H25.5V22.6C25.5 21.2 25.2 19.8 23.6 19.8C22 19.8 21.5 21 21.5 22.4V28H18V17Z" fill="white" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Native Share Button (Mobile only) */}
                {canNativeShare && (
                    <div className="share-greeting-native">
                        <button className="share-greeting-native-btn" onClick={handleNativeShare}>
                            📱 {t.shareViaApp}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
